// =====================================================================
// Portal Files page — Dispatch 5 (2026-11-05).
//
// Near-100% visual replica of Admin Project Files page (B3aPages.jsx)
// with the authoring surface stripped:
//   * no inline edits / drag handles / Add Group button / delete icons
//   * no upload form / For Client column / sort-column delete
//   * version history surfaced inside the modal (NOT a toolbar toggle)
//
// Columns (read-only): File, Category, Source, WS, Document #,
// Current V, Filetype (size), Publication, Updated, Actions (download).
// =====================================================================
import React, { useEffect, useMemo, useState, useRef } from "react";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import axios from "axios";
import {
    ArrowLeft, Download, Search, X,
    ChevronRight, ChevronDown, ChevronLeft,
} from "lucide-react";
import { portalApi, fmtDate, API } from "./portalApi";
import { Card, PageHeader, Spinner, Button, Pill, EmptyState } from "./PortalUi";
import { usePageMeta } from "../../components/Layout";
import { usePortalFileCategories } from "./context/PortalFileCategoriesContext";
import {
    WS_NAMES, WS_LIST,
    sourceOriginLabel, bytes, TypeIcon, fileTypeDisplay, fmt,
    BATCH_DOWNLOAD_MAX_BYTES,
    sortArrow, useFilesSort,
} from "../../lib/filesCommon";
import { toast } from "sonner";


// ------------- small helpers (portal-local) --------------------------

function versionStatePill(s){
    const map = {
        "Current":"bg-charcoal text-white",
        "Superseded":"bg-[#EBE8E0] text-[#666]",
        "Withdrawn":"bg-[#EDE3E1] text-[#7A5148]",
        "Removed by Client":"bg-[#EDE3E1] text-[#7A5148]",
    };
    const cls = map[s] || "bg-white border border-[#E5E2DC] text-charcoal";
    return <span className={`px-2 py-0.5 rounded-full text-[11px] font-manrope font-semibold uppercase tracking-wide ${cls}`}>{s||"—"}</span>;
}
function pubStatePill(s){
    const map = {
        "Internal Draft":"bg-[#F5EFE7] text-[#7A6A50]",
        "Published":"bg-[#E8F0E7] text-[#3B5B37]",
        "Formally Issued":"bg-[#DDE7F3] text-[#2E4A78]",
    };
    const cls = map[s] || "bg-white border border-[#E5E2DC] text-charcoal";
    return <span className={`px-2 py-0.5 rounded-full text-[11px] font-manrope font-semibold uppercase tracking-wide ${cls}`}>{s||"—"}</span>;
}

// Persisted per-project expand/collapse state.
function useExpandState(storageKey) {
    const [state, setState] = useState(() => {
        try { const v = localStorage.getItem(storageKey);
              return v ? JSON.parse(v) : {}; }
        catch { return {}; }
    });
    useEffect(() => {
        try { localStorage.setItem(storageKey, JSON.stringify(state)); }
        catch { /* ignore */ }
    }, [storageKey, state]);
    const toggle = (k) => setState((s) => ({ ...s, [k]: !(s[k] ?? true) }));
    const isOpen = (k) => state[k] ?? true;
    return { toggle, isOpen };
}


// ==================== main page ======================================

export function PortalFiles() {
    const navigate = useNavigate();
    const location = useLocation();
    const { fileId } = useParams();
    const showModal = Boolean(fileId);
    const { byKey: catByKey, categories } = usePortalFileCategories();
    usePageMeta({ title: "Files · Studio G4 Portal",
                     robots: "noindex,nofollow" });

    // Data
    const [items, setItems] = useState([]);
    const [groups, setGroups] = useState([]);
    // Current Design Issue pointer + Current Workstage — from /file-groups
    const [currentDesignGid, setCurrentDesignGid] = useState(null);
    const [currentWorkstage, setCurrentWorkstage] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Toolbar state
    const [query, setQuery] = useState("");
    const [catFilter, setCatFilter] = useState("");
    const [wsFilter, setWsFilter] = useState(null);  // 1..7 or "unassigned"
    const { sortKey, sortDir, setSortKey, setSortDir,
             clickHeader, sortEntries } = useFilesSort(
        { initialKey: "updated_at", initialDir: "desc", byKey: catByKey });
    const [groupByWs, setGroupByWs] = useState(() => {
        try { return localStorage.getItem("portal_files_group_by_ws") !== "0"; }
        catch { return true; }
    });
    useEffect(() => {
        try { localStorage.setItem("portal_files_group_by_ws",
                                     groupByWs ? "1" : "0"); }
        catch { /* ignore */ }
    }, [groupByWs]);

    const expand = useExpandState("portal_files_group_state");

    // Multi-select (Map<fid, {size, workstage}>)
    const [selected, setSelected] = useState(() => new Map());
    const clearSelection = () => setSelected(new Map());

    // Fetch files + groups in parallel.
    const refresh = async () => {
        setLoading(true); setError(null);
        try {
            const [fr, gr] = await Promise.all([
                portalApi.files({}),
                portalApi.fileGroups().catch(() => ({ data: { items: [] } })),
            ]);
            const rows = (fr.data?.items || []).map((f) => ({
                file: f,
                // The portal list endpoint folds the current version
                // into the file row — we mirror admin's `{file,
                // versions:[cur]}` shape for component parity.
                versions: [{
                    id: f.current_version_id,
                    version_number: f.current_version_number,
                    version_state: "Current",
                    publication_state: null,
                    filename: f.filename,
                    mime_type: f.content_type,
                    size: f.size,
                    document_number: f.document_number,
                    client_visible: true,
                }],
            }));
            setItems(rows);
            setGroups(gr.data?.items || []);
            setCurrentDesignGid(gr.data?.current_design_issue_group_id || null);
            setCurrentWorkstage(
                typeof gr.data?.current_workstage === "number"
                    ? gr.data.current_workstage : null);
        } catch (e) {
            setError(e?.response?.data?.detail || e?.message || "Unavailable");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { refresh(); // eslint-disable-next-line
    }, []);

    // --- filtering + sorting (same shape as admin) ---
    const filteredItems = useMemo(() => {
        let out = items;
        const q = query.trim().toLowerCase();
        if (q) {
            out = out.filter((e) => {
                const f = e.file;
                const v = (e.versions||[])[0] || {};
                const hay = [
                    f.name, f.filename, f.category_label,
                    f.source_origin_label, f.workstage_label,
                    v.filename, v.document_number,
                ].filter(Boolean).join(" ").toLowerCase();
                return hay.includes(q);
            });
        }
        if (catFilter) out = out.filter((e) => e.file.category === catFilter);
        if (wsFilter != null) {
            if (wsFilter === "unassigned") {
                out = out.filter((e) => !e.file.originating_workstage);
            } else {
                out = out.filter((e) =>
                    e.file.originating_workstage === wsFilter);
            }
        }
        return out;
    }, [items, query, catFilter, wsFilter]);

    const sortedItems = useMemo(
        () => sortEntries(filteredItems),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [filteredItems, sortKey, sortDir, catByKey]);

    // --- groupedByWs — hierarchical (ungrouped + sub-groups) per WS
    const groupedByWs = useMemo(() => {
        const groupsByWs = new Map();
        for (const g of groups) {
            if (!groupsByWs.has(g.workstage)) groupsByWs.set(g.workstage, []);
            groupsByWs.get(g.workstage).push(g);
        }
        const rowsByWs = new Map();
        const rowsByGid = new Map();
        for (const entry of sortedItems) {
            if (entry.file.group_id) {
                if (!rowsByGid.has(entry.file.group_id))
                    rowsByGid.set(entry.file.group_id, []);
                rowsByGid.get(entry.file.group_id).push(entry);
            } else {
                const ws = entry.file.originating_workstage || "unassigned";
                if (!rowsByWs.has(ws)) rowsByWs.set(ws, []);
                rowsByWs.get(ws).push(entry);
            }
        }
        for (const list of rowsByGid.values()) {
            list.sort((a, b) =>
                (a.file.group_position ?? 0) - (b.file.group_position ?? 0));
        }
        const wsOrder = wsFilter != null
            ? [wsFilter]
            : [...WS_LIST, "unassigned"];
        const out = [];
        for (const ws of wsOrder) {
            const ungrouped = rowsByWs.get(ws) || [];
            // Portal: hide empty sub-groups entirely (admin owns lifecycle).
            const subGroups = (groupsByWs.get(ws) || [])
                .map((g) => ({ group: g, rows: rowsByGid.get(g.id) || [] }))
                .filter((sg) => sg.rows.length > 0);
            out.push({ ws, ungrouped, subGroups });
        }
        return out;
    }, [sortedItems, groups, wsFilter]);

    // ----- multi-select handlers --------------------------------------
    const toggleSelect = (file) => {
        setSelected((prev) => {
            const next = new Map(prev);
            if (next.has(file.id)) {
                next.delete(file.id);
                return next;
            }
            // Cross-WS guard — portal matches admin's "single workstage"
            // constraint for batch downloads.
            const existing = Array.from(next.values());
            if (existing.length) {
                const ws0 = existing[0].workstage;
                if (ws0 !== (file.originating_workstage || null)) {
                    toast.error(
                        "Multi-select is limited to a single workstage");
                    return prev;
                }
            }
            next.set(file.id, {
                size: file.size || 0,
                workstage: file.originating_workstage || null,
            });
            return next;
        });
    };
    const toggleSelectAll = (rows) => {
        const allIn = rows.every((e) => selected.has(e.file.id));
        setSelected((prev) => {
            const next = new Map(prev);
            if (allIn) {
                for (const e of rows) next.delete(e.file.id);
            } else {
                const existing = Array.from(next.values());
                const ws0 = existing.length ? existing[0].workstage
                    : (rows[0]?.file?.originating_workstage || null);
                for (const e of rows) {
                    if ((e.file.originating_workstage || null) !== ws0) continue;
                    next.set(e.file.id, {
                        size: e.file.size || 0,
                        workstage: e.file.originating_workstage || null,
                    });
                }
            }
            return next;
        });
    };
    const selectionTotalBytes = useMemo(() =>
        Array.from(selected.values()).reduce((s, r) => s + (r.size || 0), 0),
    [selected]);
    const selectionExceedsLimit = selectionTotalBytes > BATCH_DOWNLOAD_MAX_BYTES;

    const downloadSelected = async () => {
        if (!selected.size) return;
        if (selectionExceedsLimit) {
            toast.error("Selection exceeds 500MB download limit"); return;
        }
        try {
            const r = await portalApi.filesZip(
                Array.from(selected.keys()), []);
            const blob = new Blob([r.data], { type: "application/zip" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            const cd = r.headers?.["content-disposition"] || "";
            const m = cd.match(/filename="?([^"]+)"?/);
            a.download = m ? m[1] : "portal-files.zip";
            document.body.appendChild(a); a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            clearSelection();
        } catch (err) {
            if (err?.response?.status === 413) {
                toast.error("Selection exceeds 500MB download limit");
            } else {
                toast.error("Download failed");
            }
        }
    };

    // ----- render -----------------------------------------------------
    const tableCtx = {
        catByKey,
        selected, onToggleSelect: toggleSelect,
        onToggleSelectAll: toggleSelectAll,
        navigate,
        // Column-header sort — mirrors admin Files page behaviour.
        onHeaderClick: clickHeader, sortKey, sortDir,
        // Current Design Issue + Current Workstage badges (emerald).
        currentWorkstage, currentDesignGid,
    };

    return (
        <div className="py-8 px-4 flex flex-col gap-4"
              data-testid="portal-files-page">
            <PageHeader kicker="Files"
                         title="Project files"
                         right={<Link to="/portal" className="text-[12px] text-[#A8543F] font-manrope font-semibold flex items-center gap-1"><ArrowLeft className="w-4 h-4"/>Back</Link>} />

            {/* Toolbar — pills + search + sort + group toggle */}
            <div className="bg-white border border-[#E5E2DC] rounded-lg p-3 flex flex-col gap-3"
                  data-testid="portal-files-toolbar">
                <div className="flex items-center gap-3 flex-wrap">
                    <div className="flex items-center gap-1.5 px-3 py-2 bg-[#FDFCF9] border border-[#E5E2DC] rounded-md flex-1 min-w-[220px]">
                        <Search className="w-4 h-4 text-[#999]" />
                        <input value={query}
                                onChange={(e)=>setQuery(e.target.value)}
                                data-testid="portal-files-search"
                                placeholder="Search by name, document #, category…"
                                className="flex-1 bg-transparent text-[13px] outline-none" />
                    </div>
                    <select value={`${sortKey}:${sortDir}`}
                             onChange={(e)=>{
                                 const [k, d] = e.target.value.split(":");
                                 setSortKey(k); setSortDir(d);
                             }}
                             data-testid="portal-files-sort"
                             className="border border-[#E5E2DC] rounded-md px-3 py-2 text-[13px]">
                        <option value="updated_at:desc">Updated · newest</option>
                        <option value="updated_at:asc">Updated · oldest</option>
                        <option value="name:asc">File A→Z</option>
                        <option value="name:desc">File Z→A</option>
                        <option value="category:asc">Category A→Z</option>
                        <option value="document_number:asc">Document # A→Z</option>
                    </select>
                    <label className="inline-flex items-center gap-2 text-[12px] text-[#666]">
                        <input type="checkbox"
                                data-testid="portal-files-group-by-ws"
                                checked={groupByWs}
                                onChange={(e)=>setGroupByWs(e.target.checked)} />
                        Group by workstage
                    </label>
                </div>
                {/* Category pills */}
                <div className="flex items-center gap-1.5 flex-wrap"
                      data-testid="portal-files-category-pills">
                    <button type="button" data-testid="portal-files-category-all"
                             onClick={()=>setCatFilter("")}
                             className={pillCls(!catFilter)}>All</button>
                    {categories.map((c) => (
                        <button key={c.key} type="button"
                                 data-testid={`portal-files-category-${c.key}`}
                                 onClick={()=>setCatFilter(
                                     catFilter === c.key ? "" : c.key)}
                                 className={pillCls(catFilter === c.key)}>
                            {c.label}
                        </button>
                    ))}
                </div>
                {/* Workstage pills */}
                <div className="flex items-center gap-1.5 flex-wrap"
                      data-testid="portal-files-ws-pills">
                    <button type="button"
                             data-testid="portal-files-ws-all"
                             onClick={()=>setWsFilter(null)}
                             className={pillCls(wsFilter == null)}>All Workstages</button>
                    {WS_LIST.map((n) => (
                        <button key={n} type="button"
                                 data-testid={`portal-files-ws-${n}`}
                                 onClick={()=>setWsFilter(wsFilter === n ? null : n)}
                                 className={pillCls(wsFilter === n)}>
                            Workstage {n}
                        </button>
                    ))}
                    <button type="button"
                             data-testid="portal-files-ws-unassigned"
                             onClick={()=>setWsFilter(
                                 wsFilter === "unassigned" ? null : "unassigned")}
                             className={pillCls(wsFilter === "unassigned")}>
                        Unassigned
                    </button>
                </div>
            </div>

            {/* Selection bar */}
            {selected.size > 0 && (
                <div className="bg-charcoal text-white rounded-lg px-4 py-2 flex items-center gap-3 flex-wrap"
                      data-testid="portal-files-selection-bar">
                    <span className="font-manrope font-semibold text-[12px]">
                        {selected.size} selected · {bytes(selectionTotalBytes)}
                    </span>
                    {selectionExceedsLimit && (
                        <span className="px-2 py-0.5 rounded-full bg-terracotta text-white text-[11px] font-semibold"
                              data-testid="portal-files-selection-cap-warning">
                            Over 500MB limit
                        </span>
                    )}
                    <button type="button" onClick={clearSelection}
                             data-testid="portal-files-selection-clear"
                             className="text-[12px] underline">Clear</button>
                    <button type="button" onClick={downloadSelected}
                             disabled={selectionExceedsLimit}
                             data-testid="portal-files-selection-download"
                             className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 bg-terracotta rounded-md text-[12px] font-manrope font-semibold disabled:opacity-60">
                        <Download className="w-4 h-4" />
                        Download zip
                    </button>
                </div>
            )}

            {/* Table */}
            <div className="bg-white border border-[#E5E2DC] rounded-lg overflow-hidden"
                  data-testid="portal-files-list">
                {loading ? <div className="p-4 text-[#666] text-[13px]">Loading…</div> :
                 error ? <div className="p-4 text-terracotta text-[13px]"
                              data-testid="portal-files-error">{String(error)}</div> :
                 groupByWs ? (
                    <div className="flex flex-col">
                        {groupedByWs.map(({ ws, ungrouped, subGroups }) => (
                            <WorkstageSection key={String(ws)}
                                               ws={ws}
                                               ungrouped={ungrouped}
                                               subGroups={subGroups}
                                               ctx={tableCtx}
                                               expand={expand} />
                        ))}
                    </div>
                ) : sortedItems.length === 0 ? (
                    <div className="p-4 text-[#666] text-[13px]"
                          data-testid="portal-files-list-empty">
                        No files match the current view.
                    </div>
                ) : (
                    <FileTable rows={sortedItems} ctx={tableCtx} />
                )}
            </div>

            {showModal && (
                <FileDetailModal
                    fileId={fileId} sortedItems={sortedItems}
                    activeTab={location.pathname.endsWith("/versions")
                                ? "versions" : "preview"}
                    onNavigate={(nextFid)=>navigate(
                        `/portal/files/${nextFid}`, { replace: true })}
                    onClose={()=>navigate("/portal/files")}
                />
            )}
        </div>
    );
}


// ------------- pill styling ------------------------------------------
function pillCls(active) {
    return `px-2.5 py-1 rounded-full text-[11.5px] font-manrope font-semibold uppercase tracking-wide border transition ${
        active
            ? "bg-charcoal text-white border-charcoal"
            : "bg-white text-[#666] border-[#E5E2DC] hover:border-charcoal"
    }`;
}


// ==================== Workstage section + sub-groups =================

function WorkstageSection({ ws, ungrouped, subGroups, ctx, expand }) {
    const label = ws === "unassigned"
        ? "Unassigned"
        : `WS ${ws} · ${WS_NAMES[ws] || ""}`;
    const ekey = `ws-${ws}`;
    const open = expand.isOpen(ekey);
    const allRows = [
        ...ungrouped,
        ...subGroups.reduce((acc, sg) => acc.concat(sg.rows), []),
    ];
    const isCurrentWs = ws !== "unassigned"
        && typeof ctx.currentWorkstage === "number"
        && ws === ctx.currentWorkstage;
    return (
        <div className="border-b border-[#E5E2DC] last:border-b-0"
              data-testid={`portal-files-ws-section-${ws}`}>
            <button type="button" onClick={()=>expand.toggle(ekey)}
                     data-testid={`portal-files-ws-section-toggle-${ws}`}
                     className="w-full bg-[#C28779] text-white px-4 py-2 flex items-center justify-between text-left">
                <div className="flex items-center gap-2 font-manrope font-semibold text-[12px] uppercase tracking-wide">
                    {open ? <ChevronDown className="w-4 h-4" />
                            : <ChevronRight className="w-4 h-4" />}
                    {label}
                    {isCurrentWs && (
                        <span className="inline-flex items-center px-2 py-0.5 text-[10px] font-manrope font-semibold uppercase tracking-wide rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200"
                               data-testid={`portal-files-ws-${ws}-current-workstage-badge`}>
                            Current Workstage
                        </span>
                    )}
                </div>
                <span className="text-[11px] opacity-80">
                    {allRows.length} file{allRows.length===1?"":"s"}
                </span>
            </button>
            {open && (
                <div>
                    {allRows.length === 0 ? (
                        <div className="p-3 text-[#666] text-[12px]"
                              data-testid={`portal-files-ws-${ws}-empty`}>
                            No files in this workstage.
                        </div>
                    ) : (
                        <>
                            {/* Single hoisted column header for the whole section */}
                            <FileTable rows={allRows} ctx={ctx} headerOnly
                                        wsScope={ws === "unassigned" ? "unassigned" : `ws${ws}`} />
                            {ungrouped.length > 0 && (
                                <FileTable rows={ungrouped} ctx={ctx} showHeader={false} />
                            )}
                            {subGroups.map((sg) => (
                                <SubGroupSection key={sg.group.id}
                                                   group={sg.group}
                                                   rows={sg.rows}
                                                   ctx={ctx}
                                                   expand={expand} />
                            ))}
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

function SubGroupSection({ group, rows, ctx, expand }) {
    const { selected, onToggleSelectAll } = ctx;
    const ekey = `grp-${group.id}`;
    const open = expand.isOpen(ekey);
    const allIn = rows.length > 0 && rows.every((e) => selected.has(e.file.id));
    return (
        <div className="border-t border-[#E5E2DC]"
              data-testid={`portal-files-group-${group.id}`}>
            <div className="w-full bg-[#F0ECE3] text-charcoal pl-10 pr-4 py-1.5 flex items-center gap-2">
                <input type="checkbox"
                        data-testid={`portal-files-group-select-${group.id}`}
                        checked={allIn}
                        onChange={()=>onToggleSelectAll(rows)}
                        onClick={(ev)=>ev.stopPropagation()} />
                <button type="button" onClick={()=>expand.toggle(ekey)}
                         data-testid={`portal-files-group-toggle-${group.id}`}
                         className="flex items-center gap-2 font-manrope font-semibold text-[12px] flex-1 text-left">
                    {open ? <ChevronDown className="w-4 h-4" />
                            : <ChevronRight className="w-4 h-4" />}
                    {group.name}
                    {group.is_current_design_issue && (
                        <span className="inline-flex items-center px-2 py-0.5 text-[10px] font-manrope font-semibold uppercase tracking-wide rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200"
                               data-testid={`portal-files-group-${group.id}-current-design-issue-badge`}>
                            Current Design Issue
                        </span>
                    )}
                </button>
                <span className="text-[11px] text-[#666]">
                    {rows.length} file{rows.length===1?"":"s"}
                </span>
            </div>
            {open && <FileTable rows={rows} ctx={ctx} showHeader={false} />}
        </div>
    );
}


// ==================== FileTable + FileRow ============================

function FileTable({ rows, ctx, showHeader = true, headerOnly = false, wsScope }) {
    const { onHeaderClick, sortKey, sortDir,
             selected, onToggleSelectAll } = ctx;
    const allIn = rows.length > 0 && rows.every((e) => selected.has(e.file.id));
    const selectAllTestid = wsScope
        ? `portal-files-select-all-${wsScope}`
        : "portal-files-select-all";
    return (
        <table className="w-full table-fixed text-[13px] text-left">
            <colgroup>
                <col style={{width:"3%"}} />
                <col style={{width:"26%"}} />
                <col style={{width:"12%"}} />
                <col style={{width:"10%"}} />
                <col style={{width:"5%"}} />
                <col style={{width:"9%"}} />
                <col style={{width:"5%"}} />
                <col style={{width:"13%"}} />
                <col style={{width:"11%"}} />
                <col style={{width:"6%"}} />
            </colgroup>
            {showHeader && (
                <thead className="text-[#666] bg-[#FDFCF9]">
                    <tr>
                        <th className="font-medium py-2 px-3 border-b border-[#E5E2DC]">
                            <input type="checkbox"
                                    data-testid={selectAllTestid}
                                    checked={allIn}
                                    onChange={()=>onToggleSelectAll(rows)} />
                        </th>
                        <th onClick={()=>onHeaderClick("name")}
                            data-testid="portal-files-th-name"
                            className="font-medium py-2 px-4 border-b border-[#E5E2DC] cursor-pointer select-none">File{sortArrow(sortKey==="name", sortDir)}</th>
                        <th onClick={()=>onHeaderClick("category")}
                            data-testid="portal-files-th-category"
                            className="font-medium py-2 px-4 border-b border-[#E5E2DC] cursor-pointer select-none">Category{sortArrow(sortKey==="category", sortDir)}</th>
                        <th className="font-medium py-2 px-4 border-b border-[#E5E2DC]">Source</th>
                        <th className="font-medium py-2 px-4 border-b border-[#E5E2DC]">Workstage</th>
                        <th onClick={()=>onHeaderClick("document_number")}
                            data-testid="portal-files-th-document-number"
                            className="font-medium py-2 px-4 border-b border-[#E5E2DC] cursor-pointer select-none">Document #{sortArrow(sortKey==="document_number", sortDir)}</th>
                        <th className="font-medium py-2 px-4 border-b border-[#E5E2DC]">Version</th>
                        <th className="font-medium py-2 px-4 border-b border-[#E5E2DC]">Filetype</th>
                        <th onClick={()=>onHeaderClick("updated_at")}
                            data-testid="portal-files-th-updated"
                            className="font-medium py-2 px-4 border-b border-[#E5E2DC] cursor-pointer select-none">Updated{sortArrow(sortKey==="updated_at", sortDir)}</th>
                        <th className="font-medium py-2 px-4 border-b border-[#E5E2DC]">Actions</th>
                    </tr>
                </thead>
            )}
            {!headerOnly && (
                <tbody>
                    {rows.map((entry) => (
                        <FileRow key={entry.file.id}
                                 file={entry.file} versions={entry.versions}
                                 ctx={ctx} />
                    ))}
                </tbody>
            )}
        </table>
    );
}

function FileRow({ file, versions, ctx }) {
    const { selected, onToggleSelect, catByKey, navigate } = ctx;
    const cur = (versions||[])[0];
    const ft = fileTypeDisplay(file, cur);
    const isSelected = selected.has(file.id);
    return (
        <tr className="hover:bg-[#FDFCF9] cursor-pointer"
             onClick={()=>navigate(`/portal/files/${file.id}`)}
             data-testid={`portal-files-row-${file.id}`}>
            <td className="py-2 px-3" onClick={(ev)=>ev.stopPropagation()}>
                <input type="checkbox"
                        data-testid={`portal-files-row-select-${file.id}`}
                        checked={isSelected}
                        onChange={()=>onToggleSelect(file)} />
            </td>
            <td className="py-2 px-4 truncate" title={file.name || file.filename}>
                <div className="flex items-center gap-2 min-w-0">
                    <TypeIcon mime={cur?.mime_type || file.content_type} />
                    <span className="font-semibold truncate"
                           data-testid={`portal-files-name-${file.id}`}>
                        {file.name || file.filename}
                    </span>
                </div>
            </td>
            <td className="py-2 px-4 truncate"
                 title={catByKey?.[file.category]?.label || file.category_label || "—"}>
                {catByKey?.[file.category]?.label || file.category_label || "—"}
            </td>
            <td className="py-2 px-4 truncate"
                 title={file.source_origin_label || sourceOriginLabel(file.source_origin)}>
                {file.source_origin_label || sourceOriginLabel(file.source_origin)}
            </td>
            <td className="py-2 px-4">{file.originating_workstage || "—"}</td>
            <td className="py-2 px-4 truncate"
                 title={cur?.document_number || "—"}>
                {cur?.document_number || "—"}
            </td>
            <td className="py-2 px-4">v{cur?.version_number || 1}</td>
            <td className="py-2 px-4 truncate" title={ft}
                 data-testid={`portal-files-filetype-${file.id}`}>
                {ft}
            </td>
            <td className="py-2 px-4 truncate"
                 title={fmt(file.updated_at || file.created_at)}>
                {fmt(file.updated_at || file.created_at)}
            </td>
            <td className="py-2 px-4" onClick={(ev)=>ev.stopPropagation()}>
                <a href={portalApi.fileDownloadUrl(file.id)}
                    target="_blank" rel="noreferrer"
                    data-testid={`portal-files-row-download-${file.id}`}
                    title="Download"
                    className="text-[#666] hover:text-charcoal p-1 inline-block">
                    <Download className="w-4 h-4" />
                </a>
            </td>
        </tr>
    );
}


// ==================== File detail modal ==============================
// Side panel layout matching admin FileDetailModal + sub-group siblings
// navigation + single-file download.
// =====================================================================

function FileDetailModal({ fileId, sortedItems, activeTab,
                             onNavigate, onClose }) {
    const [data, setData] = useState(null);
    const [detail, setDetail] = useState(null);
    const [versions, setVersions] = useState(null);
    const [err, setErr] = useState(null);
    const [tab, setTab] = useState(activeTab || "preview");

    const openEntry = (sortedItems || []).find(
        (e) => e.file.id === fileId);
    const groupId = openEntry?.file?.group_id || null;
    const siblings = groupId
        ? (sortedItems || []).filter((e) => e.file.group_id === groupId)
        : [];
    const idx = siblings.findIndex((e) => e.file.id === fileId);
    const hasNav = Boolean(groupId) && siblings.length > 1 && idx >= 0;
    const prevFid = hasNav && idx > 0 ? siblings[idx - 1].file.id : null;
    const nextFid = hasNav && idx < siblings.length - 1 ? siblings[idx + 1].file.id : null;

    // Fetch preview + detail + versions when fileId changes.  The
    // preview endpoint returns preview-specific state (preview_url,
    // withdrawn state) only; fileDetail returns the full
    // `_file_row_client_safe` projection used for the header +
    // metadata panel.  Both are required — the preview response is
    // deliberately narrow.
    useEffect(() => {
        let cancel = false;
        setData(null); setDetail(null); setVersions(null); setErr(null);
        portalApi.filePreview(fileId)
            .then((r) => { if (!cancel) setData(r.data); })
            .catch((e) => {
                if (cancel) return;
                if (e?.response?.status === 404) setErr("not_found");
                else setErr(e?.response?.data?.detail || "Unavailable");
            });
        portalApi.fileDetail(fileId)
            .then((r) => { if (!cancel) setDetail(r.data?.file || null); })
            .catch(() => { /* non-fatal — preview carries enough to render */ });
        portalApi.fileVersions(fileId)
            .then((r) => { if (!cancel) setVersions(r.data?.versions || []); })
            .catch(() => { /* non-fatal */ });
        return () => { cancel = true; };
    }, [fileId]);

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape") { onClose(); return; }
            if (!hasNav) return;
            if (e.key === "ArrowLeft" && prevFid) {
                e.preventDefault(); onNavigate(prevFid);
            } else if (e.key === "ArrowRight" && nextFid) {
                e.preventDefault(); onNavigate(nextFid);
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose, onNavigate, hasNav, prevFid, nextFid]);

    const openFilename = openEntry?.file?.name
                             || openEntry?.file?.filename;
    // Modal header title prefers the file row's display `name` and
    // falls back through `filename` → the sidebar sibling label →
    // finally the file ID.  Fixes the regression where the UUID was
    // being rendered as the title (Fix 3).
    const headerTitle = detail?.name
        || detail?.filename
        || openEntry?.file?.name
        || openEntry?.file?.filename
        || data?.filename
        || fileId;
    const downloadHref = `${API}/files/${fileId}/download`;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
              data-testid="portal-file-detail-modal"
              onClick={(e)=>{ if (e.target === e.currentTarget) onClose(); }}>
            {/* Fixed outer frame — the modal does not resize as content
                loads or when switching tabs.  Inner scroll regions
                handle oversized previews / long version lists. */}
            <div className="bg-[#F5F3EE] border border-[#E5E2DC] rounded-lg shadow-2xl w-[90vw] max-w-[1200px] h-[92vh] p-5 flex flex-col gap-3 overflow-hidden">
                <div className="flex items-center justify-between gap-3 shrink-0">
                    <div className="font-manrope font-semibold text-[11px] uppercase tracking-wide text-[#666]">File detail</div>
                    {hasNav && (
                        <div className="flex items-center gap-2 ml-auto"
                              data-testid="portal-file-detail-modal-nav">
                            <button type="button"
                                     onClick={()=>prevFid && onNavigate(prevFid)}
                                     disabled={!prevFid}
                                     data-testid="portal-file-detail-modal-prev"
                                     title="Previous (←)"
                                     className="text-[#666] hover:text-charcoal p-1 disabled:opacity-30 disabled:cursor-not-allowed">
                                <ChevronLeft className="w-5 h-5" />
                            </button>
                            <span className="text-[12px] text-[#666] min-w-0 truncate max-w-[280px]"
                                   title={openFilename}
                                   data-testid="portal-file-detail-modal-position">
                                <span className="font-manrope font-semibold">{idx + 1} of {siblings.length}</span>
                                {openFilename ? <span className="ml-2 text-[#999]">· {openFilename}</span> : null}
                            </span>
                            <button type="button"
                                     onClick={()=>nextFid && onNavigate(nextFid)}
                                     disabled={!nextFid}
                                     data-testid="portal-file-detail-modal-next"
                                     title="Next (→)"
                                     className="text-[#666] hover:text-charcoal p-1 disabled:opacity-30 disabled:cursor-not-allowed">
                                <ChevronRight className="w-5 h-5" />
                            </button>
                        </div>
                    )}
                    <a href={downloadHref}
                        target="_blank" rel="noreferrer"
                        data-testid="portal-file-detail-modal-download"
                        title="Download this file"
                        className={`${hasNav ? "" : "ml-auto"} text-[#666] hover:text-charcoal p-1`}>
                        <Download className="w-5 h-5" />
                    </a>
                    <button type="button" onClick={onClose}
                             data-testid="portal-file-detail-modal-close"
                             className="text-[#666] hover:text-charcoal p-1">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {err === "not_found" ? (
                    <EmptyState heading="File not found"
                                 note="The file you requested isn't available."
                                 testid="portal-file-detail-modal-notfound" />
                ) : err ? (
                    <EmptyState heading="Unavailable"
                                 note={String(err)}
                                 testid="portal-file-detail-modal-err" />
                ) : !data ? (
                    <Spinner testid="portal-file-detail-modal-loading" />
                ) : (
                    <>
                        <h2 className="font-manrope text-xl font-semibold text-charcoal truncate shrink-0"
                             title={headerTitle}
                             data-testid="portal-file-detail-modal-name">
                            {headerTitle}
                        </h2>
                        <div className="flex items-center gap-2 border-b border-[#E5E2DC] shrink-0">
                            <button type="button"
                                     onClick={()=>setTab("preview")}
                                     data-testid="portal-file-detail-modal-tab-preview"
                                     className={`px-3 py-2 text-[12px] font-manrope font-semibold uppercase tracking-wide border-b-2 ${tab==="preview"?"border-charcoal text-charcoal":"border-transparent text-[#666]"}`}>
                                Preview
                            </button>
                            <button type="button"
                                     onClick={()=>setTab("versions")}
                                     data-testid="portal-file-detail-modal-tab-versions"
                                     className={`px-3 py-2 text-[12px] font-manrope font-semibold uppercase tracking-wide border-b-2 ${tab==="versions"?"border-charcoal text-charcoal":"border-transparent text-[#666]"}`}>
                                Version history {versions?.length ? `(${versions.length})` : ""}
                            </button>
                        </div>
                        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-4 flex-1 min-h-0">
                            <div className="min-h-0 overflow-auto">
                                {tab === "preview" ? (
                                    <PreviewPane data={data} />
                                ) : (
                                    <VersionsPane fileId={fileId}
                                                   versions={versions} />
                                )}
                            </div>
                            <div className="min-h-0 overflow-auto">
                                <MetadataPanel data={data} detail={detail} />
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

function PreviewPane({ data }) {
    if (data.state === "withdrawn") {
        return (
            <div className="bg-[#FAF7F1] border border-dashed border-[#DAD6CD] rounded p-8 text-center text-[13px] text-[#6B6B67]"
                  data-testid="portal-file-preview-withdrawn">
                This version has been withdrawn and is no longer available
                to view. {data.message}
            </div>
        );
    }
    if (!data.preview_url) {
        return (
            <div className="bg-[#FAF7F1] border border-dashed border-[#DAD6CD] rounded p-8 text-center text-[13px] text-[#6B6B67]"
                  data-testid="portal-file-preview-fallback">
                Preview isn't supported for this file type.
                Please download to view offline.
            </div>
        );
    }
    const src = `${process.env.REACT_APP_BACKEND_URL || ""}${data.preview_url}`;
    if ((data.content_type || "").startsWith("image/")) {
        return <img src={src} alt={data.filename || "preview"}
                     data-testid="portal-file-preview-image"
                     className="w-full max-h-[70vh] object-contain bg-[#FAF7F1] rounded" />;
    }
    return <iframe src={src} title={data.filename || "preview"}
                    data-testid="portal-file-preview-iframe"
                    className="w-full h-[70vh] bg-[#FAF7F1] rounded border border-[#EEEBE4]" />;
}

function VersionsPane({ fileId, versions }) {
    if (!versions) return <Spinner testid="portal-file-versions-loading" />;
    if (versions.length === 0) {
        return <div className="p-6 text-[13px] text-[#666]"
                     data-testid="portal-file-versions-empty">
            No previous versions.
        </div>;
    }
    return (
        <div className="divide-y divide-[#EEEBE4] border border-[#E5E2DC] rounded"
              data-testid="portal-file-versions-list">
            {versions.map((v) => (
                <div key={v.id}
                     data-testid={`portal-file-version-${v.id}`}
                     className="px-4 py-3 flex items-center justify-between gap-3">
                    <div>
                        <div className="font-manrope text-[13px] text-charcoal">
                            Version {v.version_number}
                        </div>
                        <div className="text-[11.5px] text-[#8A8A84] mt-0.5">
                            {v.state === "withdrawn" ? "Withdrawn " : "Issued "}
                            {fmtDate(v.withdrawn_at || v.issued_at || v.created_at)}
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        {versionStatePill(
                            v.state === "withdrawn" ? "Withdrawn"
                                : v.state === "current" ? "Current"
                                : "Superseded")}
                        <a href={`${API}/files/${fileId}/preview-stream?version_id=${v.id}`}
                            target="_blank" rel="noreferrer"
                            data-testid={`portal-file-version-view-${v.id}`}
                            className="text-[11.5px] font-manrope font-semibold text-[#A8543F] hover:underline">
                            Open
                        </a>
                    </div>
                </div>
            ))}
        </div>
    );
}

function MetadataPanel({ data, detail }) {
    // `detail` is the full `/portal/files/{id}` projection and holds
    // every metadata label.  `data` is the narrower preview response
    // and carries preview-specific state (content_type, size, issued_at).
    // Fall through gracefully while detail is still loading — the
    // preview response has enough to render Filetype and dates.
    const d = detail || {};
    const pub = d.publication_state;
    const filetype = fileTypeDisplay({
        content_type: d.content_type || data.content_type,
        size: d.size ?? data.size,
        // Prefer detail's filename (version-derived) so JPEG/PDF/DOCX
        // resolve correctly even when the file row's own filename is
        // extensionless.
        filename: d.filename || data.filename,
        name: d.name,
    }, null);
    return (
        <dl className="text-[12px] bg-white border border-[#E5E2DC] rounded p-3 grid grid-cols-[auto_1fr] gap-x-3 gap-y-2"
             data-testid="portal-file-detail-metadata">
            <dt className="text-[#666]">Category</dt>
            <dd>{d.category_label || "—"}</dd>
            <dt className="text-[#666]">Workstage</dt>
            <dd>{d.workstage_label || "—"}</dd>
            <dt className="text-[#666]">Source</dt>
            <dd>{d.source_origin_label || "—"}</dd>
            <dt className="text-[#666]">Document #</dt>
            <dd>{d.document_number || "—"}</dd>
            <dt className="text-[#666]">Version</dt>
            <dd>{d.current_version_number || data.version_number || 1}</dd>
            <dt className="text-[#666]">Version state</dt>
            <dd>{d.version_state_label || (data.state === "withdrawn" ? "Withdrawn" : "Current")}</dd>
            <dt className="text-[#666]">Publication</dt>
            <dd>{pub ? pubStatePill(pub) : "—"}</dd>
            <dt className="text-[#666]">Filetype</dt>
            <dd>{filetype}</dd>
            <dt className="text-[#666]">Issued</dt>
            <dd>{fmtDate(data.issued_at)}</dd>
            <dt className="text-[#666]">Updated</dt>
            <dd>{fmtDate(d.updated_at || data.updated_at)}</dd>
        </dl>
    );
}
